/**
 * RunState: everything about the current playthrough (persisted at each stage
 * start so "Continue" resumes there). Profile: cross-run progress + settings.
 */
import { TRAITS, type TraitStat } from './traits';
import { ROOT_STAGE, isStageId, type StageId } from './tree';

const RUN_KEY = 'evo.run.v1';
const PROFILE_KEY = 'evo.profile.v1';

export interface RunData {
  version: 1;
  seed: number;
  stage: StageId;
  /** Stages visited in order, including the current one. */
  history: StageId[];
  traits: string[];
  /** Affinity points for the CURRENT stage's branch keys. Reset on stage change. */
  affinity: Record<string, number>;
  /** Number of deaths ("devolutions") across the run. */
  devolutions: number;
  /** Total active play time in seconds. */
  playTime: number;
  startedAt: number;
}

export type QualityPreset = 'auto' | 'low' | 'medium' | 'high';

export interface Settings {
  masterVolume: number;
  musicVolume: number;
  sfxVolume: number;
  voiceVolume: number;
  quality: QualityPreset;
  subtitles: boolean;
  cameraSensitivity: number;
  invertY: boolean;
  screenShake: boolean;
  reducedMotion: boolean;
  showFps: boolean;
}

export interface ProfileData {
  version: 1;
  /** Stages the player has ever reached (drives the codex/tree screen). */
  discovered: StageId[];
  /** Endings reached. */
  endings: StageId[];
  settings: Settings;
  /** Best total time (seconds) to reach each ending. */
  bestTimes: Partial<Record<StageId, number>>;
  runsStarted: number;
}

export const DEFAULT_SETTINGS: Settings = {
  masterVolume: 0.8,
  musicVolume: 0.7,
  sfxVolume: 0.9,
  voiceVolume: 0.9,
  quality: 'auto',
  subtitles: true,
  cameraSensitivity: 1,
  invertY: false,
  screenShake: true,
  reducedMotion: false,
  showFps: false,
};

function safeStorage(): Storage | undefined {
  try {
    return typeof localStorage !== 'undefined' ? localStorage : undefined;
  } catch {
    return undefined;
  }
}

export class RunState {
  data: RunData;

  constructor(data?: Partial<RunData>) {
    this.data = {
      version: 1,
      seed: Math.floor(Math.random() * 2 ** 31),
      stage: ROOT_STAGE,
      history: [ROOT_STAGE],
      traits: [],
      affinity: {},
      devolutions: 0,
      playTime: 0,
      startedAt: Date.now(),
      ...data,
    };
  }

  get stage(): StageId {
    return this.data.stage;
  }

  hasTrait(id: string): boolean {
    return this.data.traits.includes(id);
  }

  grantTrait(id: string): boolean {
    if (!TRAITS[id] || this.hasTrait(id)) return false;
    this.data.traits.push(id);
    return true;
  }

  /** Multiplier for a stat from all traits: 1 + sum(mods). dashCooldown returns e.g. 0.9. */
  mod(stat: TraitStat): number {
    let m = 1;
    for (const id of this.data.traits) m += TRAITS[id]?.mods[stat] ?? 0;
    return Math.max(0.25, m);
  }

  addAffinity(key: string, amount: number): void {
    this.data.affinity[key] = Math.max(0, (this.data.affinity[key] ?? 0) + amount);
  }

  enterStage(id: StageId): void {
    this.data.stage = id;
    if (this.data.history[this.data.history.length - 1] !== id) this.data.history.push(id);
    this.data.affinity = {};
  }

  save(): void {
    safeStorage()?.setItem(RUN_KEY, JSON.stringify(this.data));
  }

  static load(): RunState | undefined {
    const raw = safeStorage()?.getItem(RUN_KEY);
    if (!raw) return undefined;
    try {
      const d = JSON.parse(raw) as RunData;
      if (d.version !== 1 || !isStageId(d.stage)) return undefined;
      return new RunState(d);
    } catch {
      return undefined;
    }
  }

  static clear(): void {
    safeStorage()?.removeItem(RUN_KEY);
  }
}

export class Profile {
  data: ProfileData;

  constructor() {
    this.data = Profile.read();
  }

  private static read(): ProfileData {
    const fallback: ProfileData = {
      version: 1,
      discovered: [],
      endings: [],
      settings: { ...DEFAULT_SETTINGS },
      bestTimes: {},
      runsStarted: 0,
    };
    const raw = safeStorage()?.getItem(PROFILE_KEY);
    if (!raw) return fallback;
    try {
      const d = JSON.parse(raw) as ProfileData;
      if (d.version !== 1) return fallback;
      return { ...fallback, ...d, settings: { ...DEFAULT_SETTINGS, ...d.settings } };
    } catch {
      return fallback;
    }
  }

  get settings(): Settings {
    return this.data.settings;
  }

  discover(id: StageId): void {
    if (!this.data.discovered.includes(id)) this.data.discovered.push(id);
    this.save();
  }

  unlockEnding(id: StageId, totalSeconds: number): void {
    if (!this.data.endings.includes(id)) this.data.endings.push(id);
    const prev = this.data.bestTimes[id];
    if (prev === undefined || totalSeconds < prev) this.data.bestTimes[id] = totalSeconds;
    this.save();
  }

  updateSettings(patch: Partial<Settings>): void {
    Object.assign(this.data.settings, patch);
    this.save();
  }

  save(): void {
    safeStorage()?.setItem(PROFILE_KEY, JSON.stringify(this.data));
  }
}
